import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, FileText, ClipboardSignature, Briefcase,
  Receipt, FolderKanban, CalendarCheck, Activity, ListTodo, NotebookPen,
  Building2, LogOut, Search, ChevronRight, BookOpen, Settings as SettingsIcon, FileBarChart, ScrollText,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { ROLE_LABELS } from "../lib/constants";
import { NotificationsBell } from "./NotificationsBell";
import api from "../lib/api";

const navConfig = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, perm: "team_any" },
  { to: "/leads", label: "Leads", icon: Users, perm: "leads" },
  { to: "/proposals", label: "Proposals", icon: FileText, perm: "proposals" },
  { to: "/work-orders", label: "Work Orders", icon: ClipboardSignature, perm: "work_orders" },
  { to: "/projects", label: "Projects", icon: Briefcase, perm: "projects" },
  { to: "/invoices", label: "Billing", icon: Receipt, perm: "invoices" },
  { to: "/compliance", label: "Compliance", icon: CalendarCheck, perm: "compliance" },
  { to: "/monitoring", label: "Monitoring", icon: Activity, perm: "compliance" },
  { to: "/tasks", label: "Tasks", icon: ListTodo, perm: "tasks" },
  { to: "/dar", label: "DAR", icon: NotebookPen, perm: "team_any" },
  { to: "/mom", label: "MOM", icon: FolderKanban, perm: "team_any" },
  { to: "/reports", label: "Reports", icon: FileBarChart, perm: "reports" },
  { to: "/team", label: "Team", icon: Users, perm: "team" },
  { to: "/repository", label: "Repository", icon: BookOpen, perm: "team_any" },
  { to: "/audit", label: "Audit Log", icon: ScrollText, roles: ["super_admin", "hod"] },
  { to: "/settings", label: "Settings", icon: SettingsIcon, roles: ["super_admin"] },
  { to: "/portal", label: "My Portal", icon: Building2, roles: ["client"] },
];

function canSee(item, user) {
  if (!user) return false;
  if (item.roles) return item.roles.includes(user.role);
  if (user.role === "client") return false;
  if (user.role === "super_admin") return true;
  if (item.perm === "team_any") return true;
  return !!user.perms?.[item.perm]?.view;
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [logoUrl, setLogoUrl] = React.useState("");

  React.useEffect(() => {
    api.get("/settings/company").then((r) => setLogoUrl(r.data.logo_url || "")).catch(() => {});
  }, []);

  const items = navConfig.filter((n) => canSee(n, user));
  const initials = user?.name?.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase() || "U";

  return (
    <div className="min-h-screen flex bg-stone-50" style={{ fontFamily: "Poppins, sans-serif" }}>
      {/* Sidebar - PT Navy */}
      <aside className="w-64 text-slate-300 flex flex-col fixed h-screen" style={{ background: "#0F2A4A" }} data-testid="sidebar">
        <div className="px-5 h-16 flex items-center gap-3 border-b border-white/10">
          {logoUrl ? (
            <img src={logoUrl} alt="PT" className="w-9 h-9 rounded-lg object-cover bg-white/5 p-1" onError={(e) => { e.target.style.display = 'none'; }} />
          ) : (
            <div className="w-9 h-9 rounded-lg flex items-center justify-center font-bold text-white" style={{ background: "#72BE44" }}>PT</div>
          )}
          <div>
            <div className="text-white font-bold text-sm tracking-tight">PT — ECMP</div>
            <div className="text-[9px] uppercase tracking-[0.18em] text-slate-400">Compliance Platform</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-0.5">
          {items.map((item) => {
            const active = loc.pathname === item.to || (item.to !== "/" && loc.pathname.startsWith(item.to));
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`group flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-all duration-200 ${
                  active
                    ? "text-white border-l-2 pl-[10px]"
                    : "hover:bg-white/5 hover:text-white"
                }`}
                style={active ? { background: "rgba(114, 190, 68, 0.12)", borderColor: "#72BE44" } : {}}
              >
                <Icon className="w-4 h-4" strokeWidth={1.8} />
                <span className="font-medium">{item.label}</span>
                {active && <ChevronRight className="w-3 h-3 ml-auto opacity-60" />}
              </Link>
            );
          })}
        </nav>
        <div className="border-t border-white/10 p-3">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-semibold" style={{ background: "#72BE44" }}>{initials}</div>
            <div className="flex-1 min-w-0">
              <div className="text-white text-sm font-medium truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 truncate">{ROLE_LABELS[user?.role]}</div>
            </div>
            <button
              onClick={async () => { await logout(); nav("/login"); }}
              data-testid="logout-btn"
              className="p-2 rounded-md hover:bg-white/10 text-slate-400 hover:text-white transition"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
          <div className="px-2 text-[9px] text-slate-500 text-center pt-1">Your Environmental<br/>Compliance Managing Partner</div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-64 flex flex-col">
        <header className="h-16 bg-white/80 backdrop-blur-md border-b border-stone-200 sticky top-0 z-30 flex items-center px-8 gap-4">
          <div className="flex-1 max-w-md relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              data-testid="global-search"
              placeholder="Search projects, clients, leads..."
              className="w-full pl-10 pr-4 py-2 text-sm bg-stone-100/70 border border-transparent rounded-lg focus:outline-none focus:bg-white focus:border-[#72BE44] transition"
            />
          </div>
          <NotificationsBell />
        </header>
        <main className="flex-1 p-8">{children}</main>
        <footer className="border-t border-stone-200 bg-white px-8 py-3 text-xs text-stone-500 flex items-center justify-between" data-testid="app-footer">
          <span><b className="text-[#0F2A4A]">PT</b> — Your Environmental Compliance Managing Partner</span>
          <span>© {new Date().getFullYear()}</span>
        </footer>
      </div>
    </div>
  );
}
